import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import editIcon from 'assets/svg/edit-icon.svg';
import RedButton from 'components/buttons/RedButton';
import { PhoneInputField } from 'components/inputs/PhoneInputField';
import PinInputField from 'components/inputs/PinInputField';
import { H4 } from 'components/typography/Headers';
import { Text1, Text1Bold } from 'components/typography/Texts';
import ResendSection from 'features/account/resend-code/ResendSection';
import { formatStringPhoneNumber } from 'utils/phoneFormatter';

import { useUpdateAndVerifyPhone } from './useUpdateAndVerifyPhone';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding: 0 16px;
`;

const Description = styled.div`
  margin: 24px;
`;

const Button = styled(RedButton)`
  height: 46px;
  margin: 24px 0;
  width: 100%;
  max-width: 395px;
`;

const PhoneNumberText = styled(Text1Bold)`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
`;

const EditIcon = styled.img`
  cursor: pointer;
  padding: 4px;
  height: 26px;
`;

const PinInput = styled(PinInputField)`
  align-self: center;
`;

interface Props {
  mobilePhone?: string;
  onConfirmed?: () => void;
}

const MIN_PHONE_LENGTH_WITH_PLUS_SIGN = 12;

const UpdateAndVerifyPhonePopup = ({ mobilePhone, onConfirmed }: Props) => {
  const { t } = useTranslation('account');

  const { updatePhone, phoneUpdateError, phoneUpdateStatus, verifyPhone, verifyPhoneStatus, resendCode, resetForm } =
    useUpdateAndVerifyPhone();
  const [phoneNumber, setPhoneNumber] = useState(mobilePhone || '');

  const isPhoneValid = phoneNumber && phoneNumber.trim().length >= MIN_PHONE_LENGTH_WITH_PLUS_SIGN;

  if (phoneUpdateStatus !== 'completed' && !mobilePhone)
    return (
      <Container data-testid='update-phone-form'>
        <H4 data-testid={'header'}>{t('verify-phone.header')}</H4>
        <Description data-testid={'description'}>
          <Text1>{t('verify-phone.description')}</Text1>
        </Description>
        <PhoneInputField
          testId={'phone-field'}
          value={phoneNumber}
          defaultCountry={'US'}
          countries={['US', 'CA']}
          placeholder={t('myProfile:form-inputs.phone-placeholder')}
          onChange={value => setPhoneNumber(value)}
          errorMessage={phoneUpdateError}
        />
        <Button onClick={() => updatePhone(phoneNumber)} disabled={!isPhoneValid}>
          {t('verify-phone.send-code')}
        </Button>
      </Container>
    );
  else
    return (
      <Container data-testid='verify-phone-form'>
        <H4 data-testid={'header'}>{t('verify-phone.header')}</H4>
        <Description data-testid={'description'}>
          <Text1>{t('verify-phone.description-pin')}</Text1>
          <PhoneNumberText>
            {formatStringPhoneNumber(phoneNumber)}{' '}
            <EditIcon data-testid={'edit-icon'} src={editIcon} onClick={resetForm} />
          </PhoneNumberText>
        </Description>
        <PinInput
          testId={'pin-field'}
          onComplete={verifyPhone}
          disabled={verifyPhoneStatus === 'inProgress' || verifyPhoneStatus === 'completed'}
          successMessage={verifyPhoneStatus === 'completed' ? t('verify-phone.correct-pin') : ''}
          errorMessage={verifyPhoneStatus === 'failed' ? t('verify-phone.wrong-pin') : ''}
        />
        <Button onClick={onConfirmed} disabled={verifyPhoneStatus !== 'completed'}>
          {t('common:confirm')}
        </Button>
        <ResendSection onResendClick={resendCode} />
      </Container>
    );
};

export default UpdateAndVerifyPhonePopup;
